export const themes = {
  dark: {
    // Base colors
    background: 'bg-slate-950',
    panel: 'bg-slate-900',
    border: 'border-slate-700',
    
    // Text colors
    text: {
      primary: 'text-white',
      secondary: 'text-slate-300',
      muted: 'text-slate-400'
    },

    // Component specific themes
    input: {
      base: `w-full px-3 py-2 rounded-md shadow-sm
             bg-slate-800 border border-slate-700 
             text-white placeholder-slate-500`,
      focus: 'focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500'
    },

    textarea: {
      base: `w-full px-3 py-2 rounded-md shadow-sm resize-none
             bg-slate-800 border border-slate-700 
             text-white placeholder-slate-500`,
      focus: 'focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500'
    },

    button: {
      base: 'px-4 py-2 rounded-md font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2',
      primary: 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500',
      outline: 'border border-slate-700 text-slate-300 hover:bg-slate-800 focus:ring-slate-500'
    },

    switch: {
      base: 'relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
      active: 'bg-blue-600',
      inactive: 'bg-slate-700',
      thumb: {
        base: 'inline-block h-4 w-4 transform rounded-full bg-white transition-transform',
        active: 'translate-x-6',
        inactive: 'translate-x-1'
      }
    },

    label: {
      base: 'block text-sm font-medium mb-1',
      default: 'text-slate-300',
      error: 'text-red-400'
    },

    card: {
      base: 'rounded-lg border shadow-sm',
      header: 'px-6 py-4 border-b',
      content: 'px-6 py-4',
      background: 'bg-slate-900',
      border: 'border-slate-700',
      hover: 'hover:bg-slate-800'
    },

    window: {
      base: 'bg-slate-900 rounded-lg shadow-lg overflow-hidden flex flex-col border border-slate-700',
      header: {
        base: 'bg-slate-700 p-1 md:p-2 flex justify-between items-center select-none flex-shrink-0',
        title: 'text-xs md:text-sm font-medium text-slate-200 px-1 md:px-2',
        controls: 'flex items-center space-x-0.5 md:space-x-1'
      },
      content: {
        base: 'relative flex-1 min-h-0 min-w-0',
        scroll: `overflow-auto overscroll-contain
                scrollbar-thin scrollbar-track-slate-800 
                scrollbar-thumb-slate-600 scrollbar-thumb-rounded`,
        padding: 'p-4'
      },
      resize: {
        handle: 'absolute bg-transparent',
        corner: 'w-2 h-2',
        edge: 'w-2 h-full'
      },
      dragging: {
        active: 'cursor-grabbing scale-[1.02]',
        inactive: 'cursor-grab scale-100',
        transition: 'transition-transform duration-200'
      }
    },

    menuBar: {
      base: 'absolute top-0 left-0 right-0 bg-slate-800 px-4 py-2 flex items-center justify-between',
      section: {
        left: 'flex items-center space-x-4',
        center: 'flex-1 flex justify-center',
        right: 'flex items-center space-x-4'
      },
      button: {
        base: 'text-white text-sm px-3 py-1 rounded transition-colors',
        hover: 'hover:bg-slate-700',
        active: 'bg-slate-700'
      },
      dropdown: {
        base: 'absolute right-0 mt-2 bg-slate-800 rounded-lg shadow-lg py-1 z-50 w-48',
        item: 'w-full px-4 py-2 text-left text-sm text-slate-300 hover:bg-slate-700',
        divider: 'border-t border-slate-700 my-1'
      }
    },

    collapsible: {
      title: 'text-slate-300',
      content: 'bg-slate-800/50'
    },

    modal: {
      overlay: 'bg-black/60 backdrop-blur-sm',
      container: 'bg-slate-900 rounded-lg shadow-xl',
      header: 'border-b border-slate-700'
    },

    taskBar: {
      button: {
        base: 'text-white text-sm px-3 py-1 rounded transition-colors',
        active: 'bg-slate-700',
        hover: 'hover:bg-slate-700'
      }
    },

    form: {
      section: 'space-y-4',
      group: 'space-y-2',
      label: 'text-sm font-medium text-slate-300',
      error: 'text-sm text-red-500 mt-1'
    },

    tooltip: {
      base: 'absolute z-50 px-2 py-1 text-xs rounded shadow-lg',
      background: 'bg-slate-800',
      text: 'text-slate-300'
    }
  }
};

// Helper function to combine multiple theme classes
export const combineThemeClasses = (theme, paths) => {
  return paths.map(path => getThemeClass(theme, path)).join(' ');
};

// Get a single theme class by path
export const getThemeClass = (theme, path) => {
  return path.split('.').reduce((obj, key) => obj?.[key], themes[theme]) || '';
};

// Get theme color value (for dynamic styles)
export const getThemeColor = (theme, path) => {
  const colorMap = {
    'slate-950': '#020617',
    'slate-900': '#0f172a',
    'slate-800': '#1e293b',
    'slate-700': '#334155',
    'slate-600': '#475569',
    'slate-500': '#64748b',
    'slate-400': '#94a3b8',
    'slate-300': '#cbd5e1',
    'blue-600': '#2563eb',
    'blue-700': '#1d4ed8',
    'blue-500': '#3b82f6'
  };

  const className = getThemeClass(theme, path);
  const color = className.split(' ')
    .find(cls => cls.startsWith('bg-') || cls.startsWith('text-') || cls.startsWith('border-'));
  
  if (!color) return null;
  
  const colorKey = color.replace(/^(bg-|text-|border-)/, '');
  return colorMap[colorKey];
};